import React, { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import './App.css';

function bufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function convertFloat32ToInt16(float32Array) {
  let l = float32Array.length;
  const int16Array = new Int16Array(l);
  while (l--) {
    int16Array[l] = Math.min(1, float32Array[l]) * 0x7FFF; // Clamp to [-1,1] and scale to 16-bit
  }
  return int16Array;
}

function resampleBuffer(inputBuffer, inputSampleRate, outputSampleRate) {
  if (!inputBuffer || inputBuffer.length === 0) {
    return new Int16Array(0); // Return empty if no input
  }
  if (inputSampleRate === outputSampleRate) {
    return inputBuffer;
  }

  const outputLength = Math.round(inputBuffer.length * outputSampleRate / inputSampleRate);
  const outputBuffer = new Int16Array(outputLength);
  // Avoid division by zero if outputLength is 1 and inputBuffer.length is also 1 (or less, though guarded above)
  const ratio = (outputLength === 1) ? 0 : (inputBuffer.length - 1) / (outputLength - 1);

  for (let i = 0; i < outputLength; i++) {
    const inputIndex = i * ratio;
    const lower = Math.floor(inputIndex);
    const upper = Math.ceil(inputIndex);
    const fraction = inputIndex - lower;

    if (lower < 0) { // Should not happen with current logic but good guard
        outputBuffer[i] = inputBuffer[0];
    } else if (upper >= inputBuffer.length) { // Boundary condition for last element
        outputBuffer[i] = inputBuffer[inputBuffer.length -1];
    } else if (lower === upper || fraction === 0) {
      outputBuffer[i] = inputBuffer[lower];
    } else {
      outputBuffer[i] = Math.round(inputBuffer[lower] * (1 - fraction) + inputBuffer[upper] * fraction);
    }
  }
  return outputBuffer;
}

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;
const CHANNEL = 'translation-demo';
const DEMO_KEY = 'v50k07whakduUSPHC1HDquqdSP08bWyaRC23W6bUwR9wNqTmrH'; 
const WS_URL_BASE = 'wss://live-translation-backend-11577856975.europe-west2.run.app';

function App() {
  const [client, setClient] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState('en-GB'); // Default speaking language
  const [targetLanguage, setTargetLanguage] = useState('it'); // Default caption language
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const audioProcessorRef = useRef(null); // To store the ScriptProcessorNode
  const audioContextRef = useRef(null); // To store AudioContext
  const mediaStreamSourceRef = useRef(null); // To store MediaStreamAudioSourceNode
  const clientTrackSampleRateRef = useRef(48000); // Store sample rate for use in onaudioprocess

  useEffect(() => {
    const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    setClient(agoraClient);

    // Event listeners for remote users
    const handleUserPublished = async (user, mediaType) => {
      // We will not automatically subscribe to audio for this demo to avoid echo
      // await agoraClient.subscribe(user, mediaType);
      // if (mediaType === 'audio') {
      //   user.audioTrack?.play();
      // }
      // We still want to know they are there for the count
      if (!remoteUsers.find(u => u.uid === user.uid)) {
        setRemoteUsers(prev => [...prev, user]);
      }
    };

    const handleUserUnpublished = (user, mediaType) => {
      // if (mediaType === 'audio') { // No need to stop if not played
      //   user.audioTrack?.stop();
      // }
      // Still remove from count if they unpublish all relevant streams
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };

    const handleUserJoined = (user) => {
      console.log('Remote user joined:', user.uid);
      // Add to remote users list for count when they join
      if (!remoteUsers.find(u => u.uid === user.uid)) {
        setRemoteUsers(prev => [...prev, user]);
      }
    };

    const handleUserLeft = (user) => {
      console.log('Remote user left:', user.uid);
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };

    agoraClient.on('user-published', handleUserPublished);
    agoraClient.on('user-unpublished', handleUserUnpublished);
    agoraClient.on('user-joined', handleUserJoined);
    agoraClient.on('user-left', handleUserLeft);

    // Construct the WebSocket URL with the demo key
    const currentWsUrl = `${WS_URL_BASE}?demo_key=${DEMO_KEY}`;
    console.log('Attempting WebSocket connection to:', currentWsUrl);
    const ws = new WebSocket(currentWsUrl);
    wsRef.current = ws;

    const sendLanguageSettings = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('Sending language settings:', { source: sourceLanguage, target: targetLanguage });
        wsRef.current.send(JSON.stringify({
          type: 'language_settings',
          sourceLanguage: sourceLanguage,
          targetLanguage: targetLanguage
        }));
      }
    };

    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnectionStatus('connected');
      sendLanguageSettings(); // Send settings on connect
    };
    ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.reason, event.code);
      setConnectionStatus('disconnected');
    };
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('error');
    };
    ws.onmessage = (event) => {
      console.log('WebSocket message received:', event.data);
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'translation') {
          // Only add translations that are meant for this client
          // The backend sends translations in the client's target language
          setTranslations(prev => [data.text, ...prev]);
        } else if (data.type === 'transcription') {
            // Optionally handle/display interim transcriptions
            console.log('Interim transcription:', data.text, 'isFinal:', data.isFinal);
        } else if (data.type === 'info') {
            console.log('Info message:', data.message);
        } else {
          console.log('Received unhandled message type:', data.type, data.message);
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };

    return () => {
      console.log('Cleaning up App component effect');
      agoraClient.off('user-published', handleUserPublished);
      agoraClient.off('user-unpublished', handleUserUnpublished);
      agoraClient.off('user-joined', handleUserJoined);
      agoraClient.off('user-left', handleUserLeft);
      
      localAudioTrack?.stop();
      localAudioTrack?.close();
      // Check if client exists and has a connectionState before trying to leave
      if (agoraClient && (agoraClient.connectionState === 'CONNECTED' || agoraClient.connectionState === 'CONNECTING')) {
        console.log('Leaving Agora channel in cleanup');
        agoraClient.leave();
      }

      if (wsRef.current) {
        console.log('Closing WebSocket in cleanup');
        wsRef.current.close();
      }
      // Clean up audio context and nodes
      audioProcessorRef.current?.disconnect();
      mediaStreamSourceRef.current?.disconnect();
      audioContextRef.current?.close().catch(e => console.warn('Error closing AudioContext:', e));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLanguage, targetLanguage]); // Re-run effect if source or target language changes

  const joinChannel = async () => {
    if (!APP_ID) {
      alert('Agora App ID is missing. Please configure it in the frontend environment variables.');
      return;
    }
    if (!client) {
        alert('Agora client not initialized yet.');
        return;
    }
    if (client.connectionState === 'CONNECTED' || client.connectionState === 'CONNECTING') {
        console.warn('Already connected or connecting to Agora channel.');
        return;
    }

    try {
      console.log('Joining Agora channel:', CHANNEL);
      await client.join(APP_ID, CHANNEL, null, null);
      console.log('Joined Agora channel successfully. UID:', client.uid);
      
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      console.log('Microphone audio track created.');

      // Get and log client's actual sample rate from the track itself
      const mediaStreamTrack = audioTrack.getMediaStreamTrack();
      const settings = mediaStreamTrack.getSettings();
      clientTrackSampleRateRef.current = settings.sampleRate || 48000;
      console.log('AudioTrack client actual sample rate:', clientTrackSampleRateRef.current);

      await client.publish([audioTrack]);
      console.log('Audio track published.');
      setLocalAudioTrack(audioTrack);

      // Send language settings upon joining channel
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('Sending language settings after join:', { source: sourceLanguage, target: targetLanguage });
        wsRef.current.send(JSON.stringify({
          type: 'language_settings',
          sourceLanguage: sourceLanguage,
          targetLanguage: targetLanguage
        }));
      }

      // Create AudioContext. Its sampleRate might differ from track, but usually aligns.
      audioContextRef.current = new AudioContext(); 
      console.log('AudioContext created. Nominal sample rate:', audioContextRef.current.sampleRate);
      
      // const mediaStreamTrack = audioTrack.getMediaStreamTrack(); // Re-use from above
      const mediaStream = new MediaStream([mediaStreamTrack]);
      mediaStreamSourceRef.current = audioContextRef.current.createMediaStreamSource(mediaStream);
      
      audioProcessorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      console.log('ScriptProcessorNode created.');
      
      mediaStreamSourceRef.current.connect(audioProcessorRef.current);
      audioProcessorRef.current.connect(audioContextRef.current.destination);

      audioProcessorRef.current.onaudioprocess = (e) => {
        const inputFloat32Array = e.inputBuffer.getChannelData(0);
        
        const pcm16DataArray = convertFloat32ToInt16(inputFloat32Array);

        // Determine the actual input sample rate for resampling.
        // clientTrackSampleRate is defined in the outer scope of joinChannel
        // and captured by this closure.
        const currentTrackSampleRate = clientTrackSampleRateRef.current;
        
        const resampledPcm16Data = resampleBuffer(pcm16DataArray, currentTrackSampleRate, 16000);

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const base64EncodedAudio = bufferToBase64(resampledPcm16Data.buffer);
          wsRef.current.send(base64EncodedAudio);
          console.log(`Audio data sent via WebSocket (Int16 @16000Hz after resampling from ${currentTrackSampleRate}Hz), base64 length: ${base64EncodedAudio.length}`);
        }
      };
      console.log('Audio processing setup complete.');
    } catch (error) {
      console.error('Error joining channel:', error);
      alert(`Error joining channel: ${error.message}. Check console and ensure Agora App ID is correct.`);
      setLocalAudioTrack(null); // Ensure localAudioTrack is null on error
    }
  };

  const leaveChannel = async () => {
    console.log('Attempting to leave channel.');
    if (!client) {
      console.warn('Leave called but client is null');
      return;
    }

    // Disconnect audio processing chain first
    audioProcessorRef.current?.disconnect();
    mediaStreamSourceRef.current?.disconnect();
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(e => console.warn('Error closing AudioContext during leave:', e));
    }
    audioProcessorRef.current = null;
    mediaStreamSourceRef.current = null;
    audioContextRef.current = null;

    try {
      if (localAudioTrack) {
        localAudioTrack.stop();
        localAudioTrack.close();
        console.log('Local audio track stopped and closed.');
      }

      if (client.connectionState === 'CONNECTED') {
        console.log('Unpublishing tracks.');
        await client.unpublish(); // Unpublish all local tracks
        console.log('Leaving Agora channel.');
        await client.leave();
        console.log('Successfully left Agora channel.');
      } else {
        console.warn('Client not in CONNECTED state, current state:', client.connectionState);
        // If already disconnected or disconnecting, just ensure UI reflects this
      }
    } catch (error) {
      console.error('Error leaving channel:', error);
      alert(`Error leaving channel: ${error.message}`);
    } finally {
      // This should always run to update UI state
      setLocalAudioTrack(null);
      setRemoteUsers([]);
      // setTranslations([]); // Decide if translations should be cleared on leave
      console.log('Finished leaveChannel logic, localAudioTrack set to null.');
    }
  };

  const handleSourceLanguageChange = (e) => {
    setSourceLanguage(e.target.value);
    // useEffect will handle sending updated settings
  };

  const handleTargetLanguageChange = (e) => {
    setTargetLanguage(e.target.value);
    // useEffect will handle sending updated settings
  };

  const handleChannelAction = () => {
    if (localAudioTrack) {
      leaveChannel();
    } else {
      joinChannel();
    }
  };

  // More robust disabled logic for the button
  const isButtonDisabled = !client || 
                         connectionStatus !== 'connected' || 
                         client.connectionState === 'CONNECTING' || 
                         client.connectionState === 'DISCONNECTING' ||
                         (localAudioTrack && client.connectionState !== 'CONNECTED'); // Disable if joined but client state is weird

  return (
    <div className="App">
      <header className="App-header">
        <h1>Live Translation Demo</h1>
        <div className="status">
          WebSocket Status: {connectionStatus} (Agora: {client ? client.connectionState : 'uninit'})
        </div>
        <div className="channel-info">
          {client && localAudioTrack ? `In Channel (${CHANNEL}) | Others in room: ${remoteUsers.length}` : 'Not in channel'}
        </div>
        <div className="controls">
          <div>
            <label htmlFor="source-language-select">I am speaking in: </label>
            <select id="source-language-select" value={sourceLanguage} onChange={handleSourceLanguageChange}>
              <option value="en-GB">English (UK)</option>
              <option value="en-US">English (US)</option>
              <option value="it-IT">Italian</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
              {/* Add more BCP-47 codes as needed for STT */}
            </select>
          </div>
          <div>
            <label htmlFor="target-language-select">Show my captions in: </label>
            <select id="target-language-select" value={targetLanguage} onChange={handleTargetLanguageChange}>
              <option value="en">English</option>
              <option value="it">Italian</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              {/* Add more 2-letter codes or BCP-47 for translation */}
            </select>
          </div>
          <button onClick={handleChannelAction} 
                  disabled={isButtonDisabled}>
            {localAudioTrack ? 'Leave Channel' : 'Join Channel'}
          </button>
        </div>
        <p className="instructions">
          Select your spoken language and desired caption language. Join the channel. Others will see your speech translated to their chosen caption language.
        </p>
        <div className="translations">
          {translations.map((text, index) => (
            <div key={index} className="translation-item">
              {text}
            </div>
          ))}
        </div>
      </header>
    </div>
  );
}

export default App; 